import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { User, Lock, Eye, EyeSlash, SignIn, Warning, EnvelopeSimple, ArrowLeft } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function LoginPage() {
  const { login, complete2FA } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [settings, setSettings] = useState({
    login_title: "Lizenz-Verwaltung",
    login_description: "Spieler- und Schiedsrichter-Lizenzen zentral verwalten und exportieren."
  });

  // 2FA state
  const [step, setStep] = useState("login"); // "login" | "2fa" | "forgot"
  const [pendingId, setPendingId] = useState(null);
  const [emailHint, setEmailHint] = useState("");
  const [code, setCode] = useState("");

  // Forgot credentials state
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMessage, setForgotMessage] = useState("");

  useEffect(() => {
    axios.get(`${API}/settings`).then(res => setSettings(res.data)).catch(() => {});
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username || !password) return;
    setError("");
    setLoading(true);

    const result = await login(username, password);
    setLoading(false);

    if (result.requires_2fa) {
      setPendingId(result.pending_id);
      setEmailHint(result.email_hint);
      setStep("2fa");
      setCode("");
    } else if (result.error) {
      setError(result.error);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!code) return;
    setError("");
    setLoading(true);

    const result = await complete2FA(pendingId, code);
    setLoading(false);

    if (result.error) {
      setError(result.error);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setError("");
    setForgotMessage("");
    setLoading(true);

    try {
      const res = await axios.post(`${API}/forgot-credentials`, { email: forgotEmail });
      setForgotMessage(res.data.message);
    } catch (err) {
      setError(err.response?.data?.detail || "Fehler beim Senden");
    } finally {
      setLoading(false);
    }
  };

  const titleText = settings.login_title || "Lizenz-Verwaltung";

  return (
    <div className="min-h-screen flex">
      {/* Left side */}
      <div
        className="hidden lg:flex lg:w-1/2 relative"
        style={{ background: 'linear-gradient(135deg, #8b1a1a 0%, #2d0a0a 100%)' }}
      >
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <h1 className="text-5xl font-black uppercase tracking-tight leading-none mb-4" style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.3)' }}>
            {titleText}
          </h1>
          <p className="text-lg text-white/80 max-w-md">
            {settings.login_description || "Spieler- und Schiedsrichter-Lizenzen zentral verwalten und exportieren."}
          </p>
        </div>
      </div>

      {/* Right side */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden text-center mb-8">
            <h1 className="text-3xl font-black uppercase tracking-tight">{titleText}</h1>
          </div>

          <div className="bg-card border border-border rounded p-8 space-y-6 animate-fade-in">

            {/* === LOGIN STEP === */}
            {step === "login" && (
              <>
                <div className="text-center">
                  <h2 className="text-2xl font-bold uppercase tracking-tight">Anmelden</h2>
                  <p className="text-sm text-muted-foreground mt-2">Geben Sie Ihre Zugangsdaten ein</p>
                </div>
                <form onSubmit={handleLogin} className="space-y-5">
                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/30 rounded text-destructive text-sm" data-testid="login-error">
                      <Warning className="h-4 w-4 flex-shrink-0" weight="fill" />{error}
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="username" className="uppercase tracking-[0.15em] text-xs font-semibold">Benutzername</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input id="username" data-testid="login-username-input" type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Benutzername" className="pl-10 h-11 input-focus" autoComplete="username" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password" className="uppercase tracking-[0.15em] text-xs font-semibold">Passwort</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input id="password" data-testid="login-password-input" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Passwort" className="pl-10 pr-10 h-11 input-focus" autoComplete="current-password" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" data-testid="toggle-password-visibility">
                        {showPassword ? <EyeSlash className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" data-testid="login-submit-button" disabled={loading || !username || !password} className="w-full h-11 rounded-full font-semibold uppercase tracking-wider btn-primary">
                    {loading ? (
                      <span className="flex items-center gap-2"><div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />Anmelden...</span>
                    ) : (
                      <span className="flex items-center gap-2"><SignIn className="h-4 w-4" weight="bold" />Anmelden</span>
                    )}
                  </Button>
                </form>
                <div className="text-center">
                  <button data-testid="forgot-credentials-link" onClick={() => { setStep("forgot"); setError(""); setForgotMessage(""); setForgotEmail(""); }} className="text-xs text-muted-foreground hover:text-primary transition-colors underline">
                    Zugangsdaten vergessen?
                  </button>
                </div>
              </>
            )}

            {/* === 2FA STEP === */}
            {step === "2fa" && (
              <>
                <div className="text-center">
                  <h2 className="text-2xl font-bold uppercase tracking-tight">Bestätigung</h2>
                  <p className="text-sm text-muted-foreground mt-2">
                    Ein 6-stelliger Code wurde an<br /><span className="font-medium text-foreground">{emailHint}</span> gesendet.
                  </p>
                </div>
                <form onSubmit={handleVerify2FA} className="space-y-5">
                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/30 rounded text-destructive text-sm" data-testid="2fa-error">
                      <Warning className="h-4 w-4 flex-shrink-0" weight="fill" />{error}
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="code" className="uppercase tracking-[0.15em] text-xs font-semibold">Code</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input id="code" data-testid="2fa-code-input" type="text" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" className="pl-10 h-11 input-focus text-center text-xl tracking-[0.5em] font-mono" autoFocus autoComplete="one-time-code" maxLength={6} />
                    </div>
                  </div>
                  <Button type="submit" data-testid="2fa-submit-button" disabled={loading || code.length !== 6} className="w-full h-11 rounded-full font-semibold uppercase tracking-wider btn-primary">
                    {loading ? (
                      <span className="flex items-center gap-2"><div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />Prüfen...</span>
                    ) : (
                      "Bestätigen"
                    )}
                  </Button>
                </form>
                <div className="text-center">
                  <button data-testid="back-to-login" onClick={() => { setStep("login"); setError(""); setCode(""); }} className="text-xs text-muted-foreground hover:text-primary transition-colors underline flex items-center gap-1 mx-auto">
                    <ArrowLeft className="h-3 w-3" />Zurück zur Anmeldung
                  </button>
                </div>
              </>
            )}

            {/* === FORGOT CREDENTIALS STEP === */}
            {step === "forgot" && (
              <>
                <div className="text-center">
                  <h2 className="text-2xl font-bold uppercase tracking-tight">Zugangsdaten vergessen?</h2>
                  <p className="text-sm text-muted-foreground mt-2">Geben Sie Ihre registrierte E-Mail-Adresse ein.</p>
                </div>
                <form onSubmit={handleForgot} className="space-y-5">
                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/30 rounded text-destructive text-sm">
                      <Warning className="h-4 w-4 flex-shrink-0" weight="fill" />{error}
                    </div>
                  )}
                  {forgotMessage && (
                    <div className="flex items-center gap-2 p-3 bg-primary/10 border border-primary/30 rounded text-primary text-sm">
                      <EnvelopeSimple className="h-4 w-4 flex-shrink-0" weight="fill" />{forgotMessage}
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email" className="uppercase tracking-[0.15em] text-xs font-semibold">E-Mail-Adresse</Label>
                    <div className="relative">
                      <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input id="forgot-email" data-testid="forgot-email-input" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="ihre@email.de" className="pl-10 h-11 input-focus" autoFocus />
                    </div>
                  </div>
                  <Button type="submit" data-testid="forgot-submit-button" disabled={loading || !forgotEmail} className="w-full h-11 rounded-full font-semibold uppercase tracking-wider btn-primary">
                    {loading ? (
                      <span className="flex items-center gap-2"><div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />Senden...</span>
                    ) : (
                      "Zugangsdaten anfordern"
                    )}
                  </Button>
                </form>
                <div className="text-center">
                  <button data-testid="back-to-login-forgot" onClick={() => { setStep("login"); setError(""); setForgotMessage(""); }} className="text-xs text-muted-foreground hover:text-primary transition-colors underline flex items-center gap-1 mx-auto">
                    <ArrowLeft className="h-3 w-3" />Zurück zur Anmeldung
                  </button>
                </div>
              </>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
